<div>
  <div class="container-fluid">
    <!-- Page Header -->
    <div class="row mb-4">
      <div class="col">
        <h1 class="h2 mb-1 fw-bold">Account Settings</h1>
        <p class="text-muted">Manage your account security and preferences</p>
      </div>
    </div>

    <!-- Loading State -->
    <div *ngIf="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Error Message -->
    <div *ngIf="errorMessage" class="alert alert-danger alert-dismissible fade show" role="alert">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ errorMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="errorMessage = ''" aria-label="Close"></button>
    </div>

    <!-- Success Message -->
    <div *ngIf="successMessage" class="alert alert-success alert-dismissible fade show" role="alert">
      <i class="bi bi-check-circle-fill me-2"></i>
      {{ successMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="successMessage = ''" aria-label="Close"></button>
    </div>

    <!-- Settings Content -->
    <div *ngIf="!loading && profile" class="row">
      <!-- Profile Information -->
      <div class="col-md-6 mb-4">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">
              <i class="bi bi-person-circle me-2"></i>
              Profile Information
            </h5>
          </div>
          <div class="card-body">
            <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 py-2 border-bottom">
              <label class="fw-medium text-body-secondary m-0">Email</label>
              <div class="flex-grow-1 text-md-end text-break">{{ profile.email }}</div>
            </div>
            <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 py-2 border-bottom">
              <label class="fw-medium text-body-secondary m-0">Name</label>
              <div class="flex-grow-1 text-md-end text-break">{{ profile.firstName }} {{ profile.lastName }}</div>
            </div>
            <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 py-2">
              <label class="fw-medium text-body-secondary m-0">User ID</label>
              <div class="flex-grow-1 text-md-end text-break"><code>{{ profile.id }}</code></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Two-Factor Authentication -->
      <div class="col-md-6 mb-4">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">
              <i class="bi bi-shield-lock-fill me-2"></i>
              Two-Factor Authentication
            </h5>
          </div>
          <div class="card-body">
            <!-- 2FA Status -->
            <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 py-2 border-bottom">
              <label class="fw-medium text-body-secondary m-0">Status</label>
              <div class="flex-grow-1 text-md-end text-break">
                <span
                  class="badge"
                  [class.bg-success]="profile.is2FAEnabled"
                  [class.bg-secondary]="!profile.is2FAEnabled"
                >
                  {{ profile.is2FAEnabled ? 'Enabled' : 'Disabled' }}
                </span>
              </div>
            </div>

            <div *ngIf="profile.is2FAEnabled" class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 py-2 border-bottom">
              <label class="fw-medium text-body-secondary m-0">Method</label>
              <div class="flex-grow-1 text-md-end text-break">{{ get2FAMethodLabel() }}</div>
            </div>

            <!-- 2FA Actions -->
            <div class="mt-4">
              <div *ngIf="!profile.is2FAEnabled">
                <button
                  type="button"
                  class="btn btn-primary w-100"
                  (click)="navigateToSetup2FA()"
                  [disabled]="actionLoading"
                >
                  <i class="bi bi-shield-plus me-2"></i>
                  Enable Two-Factor Authentication
                </button>
                <p class="text-muted small mt-2 mb-0">
                  Add an extra layer of security to your account
                </p>
              </div>

              <div *ngIf="profile.is2FAEnabled" class="d-grid gap-2">
                <button
                  type="button"
                  class="btn btn-outline-primary"
                  (click)="regenerateBackupCodes()"
                  [disabled]="actionLoading"
                >
                  <span *ngIf="actionLoading" class="spinner-border spinner-border-sm me-2"></span>
                  <i *ngIf="!actionLoading" class="bi bi-arrow-clockwise me-2"></i>
                  Regenerate Backup Codes
                </button>
                <button
                  type="button"
                  class="btn btn-outline-danger"
                  (click)="disable2FA()"
                  [disabled]="actionLoading"
                >
                  <span *ngIf="actionLoading" class="spinner-border spinner-border-sm me-2"></span>
                  <i *ngIf="!actionLoading" class="bi bi-shield-x me-2"></i>
                  Disable Two-Factor Authentication
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Password Management -->
      <div class="col-md-6 mb-4">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">
              <i class="bi bi-key-fill me-2"></i>
              Password
            </h5>
          </div>
          <div class="card-body">
            <p class="text-muted mb-3">
              Keep your password secure and change it regularly
            </p>
            <button type="button" class="btn btn-outline-primary w-100" disabled>
              <i class="bi bi-key me-2"></i>
              Change Password <span class="badge bg-secondary ms-2">Coming Soon</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Active Sessions -->
      <div class="col-md-6 mb-4">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">
              <i class="bi bi-laptop me-2"></i>
              Active Sessions
            </h5>
          </div>
          <div class="card-body">
            <p class="text-muted mb-3">
              Manage devices and sessions where you're signed in
            </p>
            <button type="button" class="btn btn-outline-primary w-100" disabled>
              <i class="bi bi-list-ul me-2"></i>
              View Active Sessions <span class="badge bg-secondary ms-2">Coming Soon</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Security Notice -->
      <div class="col-12">
        <div class="alert alert-info">
          <h6 class="alert-heading text-primary mb-3">
            <i class="bi bi-info-circle-fill me-2"></i>
            Security Best Practices
          </h6>
          <ul class="mb-0 text-body-secondary">
            <li class="mb-2">Enable two-factor authentication for enhanced security</li>
            <li class="mb-2">Use a strong, unique password for your admin account</li>
            <li class="mb-2">Keep your backup codes in a safe, secure location</li>
            <li class="mb-2">Log out from devices you no longer use</li>
            <li>Contact your administrator if you notice any suspicious activity</li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</div>
